import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from './services/api';

import ArchiveTab from './components/ArchiveTab';
import UsersTab from './components/UsersTab';
import BorrowingTab from './components/BorrowingTab';
import ManagementTab from './components/ManagementTab';
import ReturnTab from './components/ReturnTab';
import ReservationRequestsTab from './components/ReservationRequestsTab';
import DocumentsTab from './components/DocumentsTab';

const TABS = {
  Archive: ArchiveTab,
  Users: UsersTab,
  Borrowing: BorrowingTab,
  Management: ManagementTab,
  Return: ReturnTab,
  Reservations: ReservationRequestsTab,
  Documents: DocumentsTab,
};

const TAB_LABELS = {
  Archive: 'الأرشيف',
  Users: 'المستخدمون',
  Borrowing: 'الإعارة',
  Management: 'الإدارة',
  Return: 'الإرجاع',
  Reservations: 'طلبات الحجز',
};

TAB_LABELS.Documents = 'وثائق PDF';

const sidebarVariants = {
  hidden: { x: 260 },
  visible: { x: 0, transition: { type: 'tween', duration: 0.4 } },
};

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
};

const asideVariants = {
  hidden: { x: '100%' },
  visible: { x: 0, transition: { type: 'tween', duration: 0.3 } },
};

const mainVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.25 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

export default function App() {
  const [activeTab, setActiveTab] = useState('Borrowing');
  const [newReservationCount, setNewReservationCount] = useState(0);
  const [showSidebar, setShowSidebar] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('lastActive');
    navigate('/login');
  };

  useEffect(() => {
    const updateActivity = () => localStorage.setItem('lastActive', Date.now());
    const checkInactivity = () => {
      const last = parseInt(localStorage.getItem('lastActive'), 10);
      if (last && Date.now() - last > 10 * 60 * 1000) handleLogout();
    };

    updateActivity();
    const interval = setInterval(checkInactivity, 60000);
    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keydown', updateActivity);
    return () => {
      clearInterval(interval);
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keydown', updateActivity);
    };
  }, []);

  const getViewedIds = () => {
    try {
      return JSON.parse(localStorage.getItem('viewedReservationIds')) || [];
    } catch {
      return [];
    }
  };

  const addViewedIds = (ids) => {
    const existing = getViewedIds();
    localStorage.setItem('viewedReservationIds', JSON.stringify(Array.from(new Set([...existing, ...ids]))));
  };

  useEffect(() => {
    const fetchNew = async () => {
      try {
        const res = await api.get('/reservations');
        const data = Array.isArray(res.data) ? res.data : [];
        const unseen = data.filter(r => r.status === 'pending' && !r.viewed);
        const viewedIds = getViewedIds();
        setNewReservationCount(unseen.filter(r => !viewedIds.includes(r._id)).length);
      } catch (e) {
        console.error('Error fetching reservations:', e);
      }
    };
    fetchNew();
    const iv = setInterval(fetchNew, 5000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (activeTab === 'Reservations' && newReservationCount > 0) {
      (async () => {
        try {
          const res = await api.get('/reservations');
          const data = Array.isArray(res.data) ? res.data : [];
          const unseen = data.filter(r => r.status === 'pending' && !r.viewed);
          await api.put('/reservations/mark-all-viewed');
          addViewedIds(unseen.map(r => r._id));
          setNewReservationCount(0);
        } catch (e) {
          console.error('Error marking viewed:', e);
        }
      })();
    }
  }, [activeTab, newReservationCount]);

  const ActiveComponent = TABS[activeTab];

  return (
    <div dir="rtl" className="min-h-screen flex bg-gray-100 font-sans">
      {!showSidebar && (
        <button
          onClick={() => setShowSidebar(true)}
          className="md:hidden fixed top-2 right-4 z-20 bg-blue-600 text-white p-2 rounded-full shadow-lg"
        >
          ☰
        </button>
      )}

      <AnimatePresence>
        {showSidebar && (
          <motion.div
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setShowSidebar(false)}
          >
            <motion.aside
              variants={asideVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="fixed inset-y-0 right-0 w-64 bg-white px-4 py-6 flex flex-col justify-between shadow-lg overflow-y-auto z-50 md:hidden"
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={() => setShowSidebar(false)}
                className="self-start text-gray-600 hover:text-gray-800 p-2"
              >
                ✖
              </button>

              <nav className="flex flex-col gap-3 mt-3">
                {Object.keys(TABS).map(tab => (
                  <button
                    key={tab}
                    onClick={() => { setActiveTab(tab); setShowSidebar(false); }}
                    className={`px-4 py-2 text-right rounded-lg font-medium transition ${
                      activeTab === tab
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'hover:bg-blue-100 text-gray-700'
                    }`}
                  >
                    {TAB_LABELS[tab]}
                  </button>
                ))}
              </nav>

              <button
                onClick={handleLogout}
                className="mt-6 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm text-center"
              >
                تسجيل الخروج
              </button>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.aside
        variants={sidebarVariants}
        initial="hidden"
        animate="visible"
        className="hidden md:flex w-64 bg-white border-l p-4 flex flex-col justify-between shadow-lg z-10"
      >
        <nav className="flex flex-col gap-3">
          {Object.keys(TABS).map(tab => (
            <motion.button
              key={tab}
              onClick={() => setActiveTab(tab)}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className={`px-4 py-2 text-right rounded-lg font-medium transition ${
                activeTab === tab
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'hover:bg-blue-100 text-gray-700'
              }`}
            >
              <span className="flex items-center justify-between w-full">
                {TAB_LABELS[tab]}
                {tab === 'Reservations' && newReservationCount > 0 && (
                  <span className="ml-2 bg-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {newReservationCount}
                  </span>
                )}
              </span>
            </motion.button>
          ))}
        </nav>

        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.96 }}
          onClick={handleLogout}
          className="mt-6 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm text-center"
        >
          تسجيل الخروج
        </motion.button>
      </motion.aside>

      <main className="flex-1 px-4 sm:px-6 md:px-8 py-8 overflow-y-auto min-h-screen">
        <h1 className="relative z-30 text-2xl font-bold mb-6 flex justify-center items-center gap-2 text-center">
          {TAB_LABELS[activeTab]}
        </h1>

        <motion.div
          key={activeTab}
          variants={mainVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          layout
          className="bg-white p-6 rounded-lg shadow-md space-y-6"
        >
          {ActiveComponent ? <ActiveComponent /> : <div>تبويب غير متوفّر</div>}
        </motion.div>
      </main>
    </div>
  );
}
